/**
 * EmpfangenPage — public recruit landing page (architecture-loop-a.md §9, WL-V1).
 * Reachable at `/empfangen` (top-level, NO auth). The PDF-footer short link
 * `faebill.com/r/{token}` redirects here as `/empfangen?ref={token}`.
 *
 * Behaviour:
 *  - With a valid `?ref` token → resolves it (public `resolveRecruitToken` callable)
 *    and shows a personalized hook: sender company + MASKED invoice number, plus a
 *    "receive it free" signup CTA. No invoice content beyond those two fields is
 *    ever fetched pre-auth (§7.8).
 *  - Without a token, or an invalid/expired/claimed one → the generic, SEO-friendly
 *    landing page ("E-Rechnungen kostenlos empfangen & sicher archivieren").
 *
 * Inertness: the page itself is harmless-static and always renders. While the
 * backend flag `INBOUND_ENABLED` is off the callable returns the uniform
 * `{ valid: false }`, so the page simply shows its generic variant (§12).
 *
 * The `?ref` token (when present) is stashed in `sessionStorage['recruitRef']` so
 * the post-signup onboarding can auto-claim the recruited invoice (WL-V3).
 *
 * Wording is Whitelist-compliant (§10): no naked "GoBD-konform", no standalone
 * "revisionssicher", no absolute "geprüft/rechtssicher". The archiving claim uses
 * only the allowed collocation + the mandatory Verfahrensdokumentation note.
 */

import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BrandLogo } from "../components/common/BrandLogo";
import { resolveRecruitToken, RECRUIT_REF_SESSION_KEY } from "../api/recruitApi";

/** Resolve lifecycle for the personalized hook. */
type ResolveState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "valid"; senderCompanyName: string; invoiceNumberMasked: string }
  | { status: "generic" };

const EmpfangenPage: React.FC = () => {
  const { t } = useTranslation("empfangen");
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref");
  const [state, setState] = useState<ResolveState>({ status: "idle" });

  // Persist the ref token for the post-signup auto-claim (WL-V3), regardless of
  // whether the pre-auth resolve succeeds (a transient resolve failure must not
  // lose the claim opportunity).
  useEffect(() => {
    if (ref) {
      try {
        sessionStorage.setItem(RECRUIT_REF_SESSION_KEY, ref);
      } catch {
        // Private-mode / storage-disabled → claim just won't auto-fire; harmless.
      }
    }
  }, [ref]);

  useEffect(() => {
    document.title = t("meta.title");
  }, [t]);

  // Resolve the token (or fall straight through to the generic page).
  useEffect(() => {
    let cancelled = false;
    if (!ref) {
      setState({ status: "generic" });
      return;
    }
    setState({ status: "loading" });
    resolveRecruitToken(ref)
      .then((result) => {
        if (cancelled) return;
        if (
          result.valid &&
          result.senderCompanyName &&
          result.invoiceNumberMasked
        ) {
          setState({
            status: "valid",
            senderCompanyName: result.senderCompanyName,
            invoiceNumberMasked: result.invoiceNumberMasked,
          });
        } else {
          setState({ status: "generic" });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: "generic" });
      });
    return () => {
      cancelled = true;
    };
  }, [ref]);

  return (
    <div className="min-h-screen bg-white text-[#0C1322] font-sans flex flex-col">
      {/* Header */}
      <header className="px-6 py-5 border-b border-slate-100">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <BrandLogo />
          <Link
            to="/login"
            className="text-sm font-medium text-indigo-700 hover:text-indigo-800 transition"
          >
            {t("nav.login")}
          </Link>
        </div>
      </header>

      {/* Hero */}
      <main className="flex-1">
        <section className="max-w-5xl mx-auto px-6 py-16 sm:py-24">
          {state.status === "loading" && (
            <p className="text-slate-500 text-lg" role="status">
              {t("loading")}
            </p>
          )}

          {state.status === "valid" && (
            <div className="max-w-2xl">
              <p className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 bg-indigo-50 rounded-full px-3 py-1 mb-6">
                <span aria-hidden="true">✉️</span>
                {t("personalized.eyebrow")}
              </p>
              <h1 className="text-3xl sm:text-5xl font-bold leading-tight tracking-tight">
                {t("personalized.title", {
                  sender: state.senderCompanyName,
                  masked: state.invoiceNumberMasked,
                })}
              </h1>
              <p className="mt-6 text-lg text-slate-600">
                {t("personalized.body")}
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/register"
                  className="px-6 py-3 rounded-md bg-indigo-600 hover:bg-indigo-700 text-[#0C1322] font-semibold shadow-lg hover:shadow-xl transition"
                >
                  {t("personalized.cta")}
                </Link>
                <span className="text-sm text-slate-500">
                  {t("personalized.reassurance")}
                </span>
              </div>
            </div>
          )}

          {state.status === "generic" && (
            <div className="max-w-2xl">
              <h1 className="text-3xl sm:text-5xl font-bold leading-tight tracking-tight">
                {t("generic.title")}
              </h1>
              <p className="mt-6 text-lg text-slate-600">
                {t("generic.subtitle")}
              </p>
              <div className="mt-10">
                <Link
                  to="/register"
                  className="px-6 py-3 rounded-md bg-indigo-600 hover:bg-indigo-700 text-[#0C1322] font-semibold shadow-lg hover:shadow-xl transition"
                >
                  {t("generic.cta")}
                </Link>
              </div>
            </div>
          )}
        </section>

        {/* Feature strip (shown on both variants once resolved) */}
        {state.status !== "loading" && state.status !== "idle" && (
          <section className="bg-slate-50 border-t border-slate-100">
            <div className="max-w-5xl mx-auto px-6 py-14 grid gap-8 sm:grid-cols-3">
              <Feature
                title={t("features.address.title")}
                body={t("features.address.body")}
              />
              <Feature
                title={t("features.structured.title")}
                body={t("features.structured.body")}
              />
              <Feature
                title={t("features.free.title")}
                body={t("features.free.body")}
              />
            </div>
            <div className="max-w-5xl mx-auto px-6 pb-14">
              <p className="text-sm text-slate-500">{t("compliance.claim")}</p>
              <p className="text-xs text-slate-400 mt-2">
                {t("compliance.note")}
              </p>
            </div>
          </section>
        )}
      </main>

      <footer className="px-6 py-8 border-t border-slate-100">
        <div className="max-w-5xl mx-auto text-xs text-slate-400 flex flex-wrap gap-x-6 gap-y-2">
          <span>© FaeBill</span>
          <a href="https://faebill.com/impressum" className="hover:text-slate-600">
            {t("footer.imprint")}
          </a>
          <a href="https://faebill.com/datenschutz" className="hover:text-slate-600">
            {t("footer.privacy")}
          </a>
        </div>
      </footer>
    </div>
  );
};

/** A single feature tile in the strip. */
const Feature: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <div>
    <h3 className="font-semibold text-[#0C1322]">{title}</h3>
    <p className="mt-2 text-sm text-slate-600">{body}</p>
  </div>
);

export default EmpfangenPage;
